import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ExternalLink, PenLine, X } from "lucide-react";
import { REVIEWS } from "@/data/content";
import { Reveal } from "@/lib/motion";
import Magnetic from "@/components/Magnetic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GoogleG({ className = "w-5 h-5" }) {
    return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
        </svg>
    );
}

function Stars({ n = 5, size = "w-3.5 h-3.5" }) {
    return (
        <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
                <Star key={i} className={size + (i < n ? " fill-amber-400 text-amber-400" : " text-slate-300")} />
            ))}
        </div>
    );
}

function PreviewCard({ r }) {
    return (
        <div className="w-[340px] flex-shrink-0 bg-white border border-line p-7 mx-3">
            <div className="flex items-center justify-between mb-3">
                <Quote className="w-8 h-8 text-brand-red/20" />
                <GoogleG className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-sm leading-relaxed text-slate-700 mb-5 font-body line-clamp-5">{r.text}</p>
            <div className="flex items-center justify-between pt-4 border-t border-line">
                <div className="flex items-center gap-3">
                    {r.photo && <img src={r.photo} alt="" className="w-8 h-8 rounded-full object-cover" loading="lazy" />}
                    <div>
                        <p className="text-sm font-semibold text-navy font-display">{r.name}</p>
                        <p className="text-xs text-slate-500 font-body">{r.role}</p>
                    </div>
                </div>
                <Stars n={r.rating || 5} />
            </div>
        </div>
    );
}

function AllReviewsModal({ open, onClose, reviews, rating, count }) {
    const [visible, setVisible] = useState(9);
    useEffect(() => {
        if (!open) return;
        setVisible(9);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} onClick={onClose}>
                    <div className="absolute inset-0 bg-ink/70 backdrop-blur-md" />
                    <motion.div role="dialog" aria-modal="true" aria-label="Tous les avis Google" className="relative w-full sm:max-w-3xl bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto" initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 bg-white border-b border-line px-6 py-5 flex items-center justify-between z-10">
                            <div className="flex items-center gap-3">
                                <GoogleG className="w-6 h-6" />
                                <div>
                                    <p className="text-sm font-semibold text-navy font-display">Avis Google — IGN Services Alsace</p>
                                    <p className="text-xs text-slate-500 font-body">{String(rating).replace(".", ",")} ★ · {count} avis</p>
                                </div>
                            </div>
                            <button onClick={onClose} aria-label="Fermer" data-testid="reviews-modal-close" className="w-9 h-9 flex items-center justify-center rounded-full bg-mist text-navy hover:bg-navy hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 grid sm:grid-cols-2 gap-4">
                            {reviews.slice(0, visible).map((r, i) => (
                                <div key={r.review_id || i} className="border border-line p-5" data-testid={`google-review-card-${i}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        {r.author_photo ? <img src={r.author_photo} alt="" className="w-9 h-9 rounded-full object-cover" loading="lazy" /> : <span className="w-9 h-9 rounded-full bg-navy text-white flex items-center justify-center text-sm font-semibold">{(r.author_name || "?")[0]}</span>}
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-navy font-display">{r.author_name}</p>
                                            <p className="text-xs text-slate-400 font-body">{r.relative_date}</p>
                                        </div>
                                        <GoogleG className="w-4 h-4" />
                                    </div>
                                    <Stars n={r.rating || 5} />
                                    {r.text && <p className="text-sm leading-relaxed text-slate-600 font-body mt-3">{r.text}</p>}
                                    {r.link && <a href={r.link} target="_blank" rel="noopener noreferrer" className="inline-block text-xs text-electric hover:underline font-body mt-3">Voir sur Google</a>}
                                </div>
                            ))}
                        </div>
                        {visible < reviews.length && (
                            <div className="px-6 pb-8 text-center">
                                <button onClick={() => setVisible((v) => v + 9)} data-testid="reviews-load-more" className="inline-flex items-center gap-2 border border-navy text-navy px-8 py-3.5 text-xs uppercase tracking-wide font-semibold font-body hover:bg-navy hover:text-white transition-colors duration-300">
                                    Afficher plus ({reviews.length - visible} restants)
                                </button>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
}

export default function Reviews() {
    const g = REVIEWS.google;
    const [data, setData] = useState({ configured: false, rating: g.rating, count: g.count, reviews: [] });
    const [modal, setModal] = useState(false);

    useEffect(() => {
        let alive = true;
        axios.get(`${API}/reviews`).then((res) => { if (alive) setData(res.data); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    const hasLive = data.configured && Array.isArray(data.reviews) && data.reviews.length > 0;
    const rawRating = data.rating != null ? data.rating : g.rating;
    const ratingStr = typeof rawRating === "number" ? rawRating.toFixed(1).replace(".", ",") : String(rawRating);
    const count = data.count ?? g.count;

    const previews = hasLive
        ? data.reviews.slice(0, 6).map((r) => ({ text: r.text, name: r.author_name, role: r.relative_date, photo: r.author_photo, rating: r.rating }))
        : REVIEWS.items.map((r) => ({ ...r, rating: 5 }));
    const track = [...previews, ...previews];

    return (
        <section id="avis" className="py-24 md:py-32 bg-white overflow-hidden" data-testid="reviews-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12">
                    <Reveal><span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">{REVIEWS.kicker}</span></Reveal>
                    <Reveal delay={0.05}><h2 className="text-3xl md:text-5xl font-medium tracking-tight text-navy mt-4 mb-8 font-display">{REVIEWS.title}</h2></Reveal>

                    <Reveal delay={0.1}>
                        <div className="inline-flex flex-col items-center gap-4 bg-mist border border-line px-8 py-7 rounded-2xl max-w-md mx-auto" data-testid="google-rating-block">
                            <div className="flex items-center gap-3">
                                <GoogleG className="w-7 h-7" />
                                <span className="text-sm font-semibold text-navy font-body">Google</span>
                            </div>
                            <div className="flex items-baseline gap-3">
                                <span className="text-5xl font-light text-navy font-display" data-testid="google-rating-value">{ratingStr}</span>
                                <div className="flex flex-col items-start">
                                    <Stars n={5} size="w-4 h-4" />
                                    <span className="text-sm text-slate-500 font-body mt-1" data-testid="google-review-count">{count} avis Google</span>
                                </div>
                            </div>
                            <span className="text-xs uppercase tracking-wider text-slate-400 font-body">{g.label}</span>
                            <div className="flex flex-col sm:flex-row gap-3 w-full mt-1">
                                <Magnetic strength={0.25} className="flex-1">
                                    {hasLive ? (
                                        <button onClick={() => setModal(true)} className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white px-5 py-3 text-xs uppercase tracking-wide font-semibold font-body hover:bg-electric transition-colors duration-300" data-testid="google-view-all-btn">
                                            <ExternalLink className="w-4 h-4" /> Voir tous nos avis Google
                                        </button>
                                    ) : (
                                        <a href={g.viewUrl} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white px-5 py-3 text-xs uppercase tracking-wide font-semibold font-body hover:bg-electric transition-colors duration-300" data-testid="google-view-all-btn">
                                            <ExternalLink className="w-4 h-4" /> Voir tous nos avis Google
                                        </a>
                                    )}
                                </Magnetic>
                                <a href={g.writeUrl} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 border border-navy text-navy px-5 py-3 text-xs uppercase tracking-wide font-semibold font-body hover:bg-navy hover:text-white transition-colors duration-300" data-testid="google-write-btn">
                                    <PenLine className="w-4 h-4" /> Laisser un avis Google
                                </a>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.14}><p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-body mt-10">{hasLive ? "Derniers avis Google" : REVIEWS.testimonialsLabel}</p></Reveal>
                </div>
            </div>

            <div className="marquee-track relative">
                <div className="marquee" style={{ animationDuration: "44s" }}>
                    {track.map((r, i) => <PreviewCard key={i} r={r} />)}
                </div>
                <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
                <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />
            </div>

            <AllReviewsModal open={modal} onClose={() => setModal(false)} reviews={data.reviews || []} rating={ratingStr} count={count} />
        </section>
    );
}
